import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';

type Status = 'loading' | 'success' | 'error' | 'form';

interface FormErrors {
  email?: string;
}

export const EmailVerificationPage: React.FC = () => {
  const { verifyEmail, resendVerification, error, clearError } = useAuth();
  const [searchParams] = useSearchParams();
  const tokenParam = searchParams.get('token');

  const [status, setStatus] = useState<Status>(tokenParam ? 'loading' : 'form');
  const [email, setEmail] = useState('');
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  useEffect(() => {
    if (!tokenParam) return;

    let active = true;
    verifyEmail(tokenParam)
      .then(() => { if (active) setStatus('success'); })
      .catch(() => { if (active) setStatus('error'); });

    return () => { active = false; };
  }, []); // eslint-disable-line

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: FormErrors = {};
    if (!email) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = 'Enter a valid email address';
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setIsResending(true);
    setResendSuccess(false);
    if (error) clearError();
    try {
      await resendVerification(email);
      setResendSuccess(true);
    } catch {
      // error shown via AuthContext error state
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div style={styles.root} data-testid="email-verification-page">
      <div style={styles.card}>
        {status === 'loading' && (
          <div style={styles.center} data-testid="email-verification-loading">
            <div style={styles.spinner} />
            <p style={styles.loadingText}>Verifying your email address…</p>
          </div>
        )}

        {status === 'success' && (
          <div style={styles.center} data-testid="email-verification-success">
            <div style={{ ...styles.iconCircle, background: '#d1fae5' }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="12" fill="#10b981" />
                <path d="M7 12.5l3.5 3.5 6-7" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h2 style={styles.heading}>Email Verified!</h2>
            <p style={styles.sub}>Your email has been verified successfully. You can now sign in to your account.</p>
            <Link to="/auth/login" style={styles.btn} data-testid="email-verification-success-login-btn">Go to Sign In</Link>
          </div>
        )}

        {status === 'error' && (
          <div style={styles.center} data-testid="email-verification-error">
            <div style={{ ...styles.iconCircle, background: '#fee2e2' }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="12" fill="#ef4444" />
                <path d="M8 8l8 8M16 8l-8 8" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
              </svg>
            </div>
            <h2 style={styles.heading}>Verification Failed</h2>
            <p style={styles.sub}>The link is invalid or has expired. Request a new verification link below.</p>
            {error && <p style={styles.errBox} data-testid="email-verification-error-alert">{error}</p>}
            <button style={styles.btn} onClick={() => setStatus('form')} data-testid="email-verification-request-link-btn">Request New Link</button>
            <Link to="/auth/login" style={{ ...styles.btnOutline, marginTop: 10 }} data-testid="email-verification-error-login-link">Back to Login</Link>
          </div>
        )}

        {status === 'form' && (
          <div data-testid="email-verification-form-container">
            <div style={styles.center}>
              <div style={{ ...styles.iconCircle, background: '#e0f2fe' }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="12" fill="#0ea5e9" />
                  <path d="M4 8l8 5 8-5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
                  <rect x="4" y="7" width="16" height="11" rx="2" stroke="#fff" strokeWidth="1.8" />
                </svg>
              </div>
              <h2 style={styles.heading}>Verify Your Email</h2>
              <p style={styles.sub}>Enter your email address and we'll send you a new verification link.</p>
            </div>

            {error && <p style={styles.errBox} data-testid="email-verification-error-alert">{error}</p>}
            {resendSuccess && (
              <p style={styles.successBox} data-testid="email-verification-resend-success-box">Verification email sent! Please check your inbox.</p>
            )}

            <form onSubmit={handleResend} noValidate style={{ marginTop: 20 }}>
              <label style={styles.label}>Email Address</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                style={{ ...styles.input, ...(formErrors.email ? styles.inputErr : {}) }}
                autoFocus
                autoComplete="email"
                data-testid="email-verification-email-input"
              />
              {formErrors.email && <p style={styles.fieldErr} data-testid="email-verification-email-error">{formErrors.email}</p>}

              <button
                type="submit"
                disabled={isResending}
                style={{ ...styles.btn, marginTop: 20, width: '100%', border: 'none', cursor: isResending ? 'not-allowed' : 'pointer', opacity: isResending ? 0.7 : 1 }}
                data-testid="email-verification-submit-button"
              >
                {isResending ? 'Sending…' : 'Send Verification Email'}
              </button>
              <Link
                to="/auth/login"
                style={{ ...styles.btnOutline, marginTop: 10, display: 'block', textAlign: 'center' }}
                data-testid="email-verification-back-login-link"
              >
                Back to Login
              </Link>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  root: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #1A3A52 0%, #2B9BA5 100%)',
    padding: '24px 16px',
    fontFamily: "'Hanken Grotesk', -apple-system, 'Segoe UI', sans-serif",
  },
  card: {
    background: '#fff',
    borderRadius: 16,
    padding: '48px 40px',
    maxWidth: 440,
    width: '100%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
  },
  center: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  iconCircle: {
    width: 80,
    height: 80,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  heading: {
    margin: '0 0 10px',
    fontSize: 22,
    fontWeight: 700,
    color: '#1A3A52',
  },
  sub: {
    margin: '0 0 20px',
    fontSize: 14,
    color: '#64748b',
    lineHeight: 1.6,
  },
  btn: {
    display: 'inline-block',
    padding: '12px 28px',
    background: '#2B9BA5',
    color: '#fff',
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 14,
    textDecoration: 'none',
    cursor: 'pointer',
  },
  btnOutline: {
    display: 'inline-block',
    padding: '11px 28px',
    background: 'transparent',
    color: '#2B9BA5',
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 14,
    textDecoration: 'none',
    border: '1.5px solid #2B9BA5',
  },
  spinner: {
    width: 56,
    height: 56,
    border: '5px solid #e2e8f0',
    borderTop: '5px solid #2B9BA5',
    borderRadius: '50%',
    animation: 'ev-spin 0.9s linear infinite',
    marginBottom: 20,
  },
  loadingText: {
    fontSize: 16,
    color: '#475569',
    margin: 0,
  },
  errBox: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#b91c1c',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 13,
    margin: '0 0 16px',
    width: '100%',
    boxSizing: 'border-box',
  },
  successBox: {
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    color: '#166534',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 13,
    margin: '0 0 16px',
  },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 6,
  },
  input: {
    width: '100%',
    height: 44,
    border: '1.5px solid #d1d5db',
    borderRadius: 8,
    padding: '0 14px',
    fontSize: 14,
    color: '#111827',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  inputErr: {
    borderColor: '#ef4444',
  },
  fieldErr: {
    margin: '4px 0 0',
    fontSize: 12,
    color: '#ef4444',
  },
};

// Inject spin keyframes once
if (typeof document !== 'undefined' && !document.getElementById('ev-spin-style')) {
  const s = document.createElement('style');
  s.id = 'ev-spin-style';
  s.textContent = '@keyframes ev-spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(s);
}
